import type { PageSnapshot } from "./api";

type LoginState = PageSnapshot["login_state"];

export function loginTone(state: LoginState): "ok" | "danger" | "slate" {
  if (state === "in") return "ok";
  if (state === "out") return "danger";
  return "slate";
}

export function loginLabel(state: LoginState): string {
  if (state === "in") return "Logged in";
  if (state === "out") return "Logged OUT";
  return "Unknown";
}

export interface SnapshotWarning {
  text: string;
  /** "danger" = suspect the CAPTURE; "muted" = a config gap, nothing wrong with the page. */
  tone: "danger" | "muted";
}

/**
 * A snapshot's stored login_state vs what the config markers expect (an authenticated
 * session). Non-"in" states are suspicious: the captured prices may be list/gross,
 * not the trader's negotiated net price. Returns the warning or null when fine.
 *
 * "unknown" has two very different causes and must not read as the same red alarm:
 * a config that declares NO login markers can never produce a verdict (a config gap
 * — classifyLoginState returns "unknown" by definition), whereas declared markers
 * that didn't match really is a reason to distrust the capture.
 */
export function snapshotWarn(s: PageSnapshot): SnapshotWarning | null {
  if (s.login_state === "out")
    return {
      text: "Captured while logged OUT — prices are likely list/gross, not the negotiated net price.",
      tone: "danger",
    };
  if (s.login_state === "in") return null;
  if (s.markers_note == null)
    return {
      text: "No login verdict — this vendor had no config on the server when the page was captured.",
      tone: "muted",
    };
  if (/in-marker: none/.test(s.markers_note) && /out-marker: none/.test(s.markers_note))
    return {
      text: "No login verdict — this config declares no login markers. Add a loggedInMarker to verify the session.",
      tone: "muted",
    };
  return {
    text: "Login state unknown — the config's login markers didn't match this page, so the session can't be confirmed.",
    tone: "danger",
  };
}
